// src/index.ts
import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";
var serverSchema = {
  NODE_ENV: z.enum(["development", "production", "test", "staging"]).default("development"),
  DIRECT_DATABASE_URL: z.string().url(),
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),
  POOLING_DATABASE_URL: z.string().url(),
  DB_FAILOVER_ENABLED: z.enum(["true", "false"]).default("false").transform((val) => val === "true"),
  PRIMARY_DIRECT_DATABASE_URL: z.string().url().optional(),
  PRIMARY_POOLING_DATABASE_URL: z.string().url().optional(),
  SECONDARY_DIRECT_DATABASE_URL: z.string().url().optional(),
  SECONDARY_POOLING_DATABASE_URL: z.string().url().optional(),
  DB_ACTIVE_ENDPOINT: z.enum(["primary", "secondary"]).default("primary"),
  DB_FAILOVER_FAILURE_THRESHOLD: z.coerce.number().int().min(1).default(3),
  DB_FAILOVER_COOLDOWN_MS: z.coerce.number().int().min(1e3).default(3e4),
  DB_HEALTHCHECK_INTERVAL_MS: z.coerce.number().int().min(1e3).default(1e4),
  DB_HEALTHCHECK_TIMEOUT_MS: z.coerce.number().int().min(250).default(2e3),
  DB_AUTO_FAILBACK_ENABLED: z.enum(["true", "false"]).default("false").transform((val) => val === "true"),
  SUPABASE_URL: z.string().url(),
  SUPABASE_ANON_KEY: z.string().min(1),
  STORAGE_PROVIDER: z.enum(["supabase", "r2"]).default("supabase"),
  STORAGE_PUBLIC_BASE_URL: z.string().url().optional(),
  R2_ACCOUNT_ID: z.string().min(1).optional(),
  R2_ACCESS_KEY_ID: z.string().min(1).optional(),
  R2_SECRET_ACCESS_KEY: z.string().min(1).optional(),
  R2_PRODUCT_IMAGES_BUCKET: z.string().min(1).optional(),
  R2_PRODUCT_FILES_BUCKET: z.string().min(1).optional(),
  R2_DELIVERY_PROOF_BUCKET: z.string().min(1).optional(),
  R2_PRODUCT_IMAGES_PUBLIC_BASE_URL: z.string().url().optional(),
  R2_PRODUCT_FILES_PUBLIC_BASE_URL: z.string().url().optional(),
  R2_DELIVERY_PROOF_PUBLIC_BASE_URL: z.string().url().optional(),
  LOG_LEVEL: z.enum(["trace", "debug", "info", "warn", "error", "fatal"]).default("debug"),
  FASTIFY_PORT: z.string().default("4000"),
  FASTIFY_HOST: z.string().default("0.0.0.0"),
  FASTIFY_TRUST_PROXY: z.enum(["true", "false"]).default("false").transform((val) => val === "true"),
  COOKIE_SECRET: z.string().min(1),
  RESEND_API_KEY: z.string().min(1),
  AUTH_COOKIE_KEY: z.string().min(1),
  // Redis connections - not using .url() to support Railway private connections
  REDIS_URL: z.string().min(1),
  REDIS_HOST: z.string().optional(),
  REDIS_PORT: z.string().optional(),
  REDIS_PASSWORD: z.string().optional(),
  REDIS_DB: z.string().optional(),
  EVENTS_REDIS_URL: z.string().min(1),
  JOBS_REDIS_URL: z.string().min(1),
  LOG_TARGET: z.enum(["stdout", "http", "betterstack", "otlp", "otel"]).default("stdout"),
  B2BWAVE_API_KEY: z.string().min(1),
  B2BWAVE_API_URL: z.string().url(),
  B2BWAVE_API_TOKEN: z.string().min(1),
  B2BWAVE_APP_CODE: z.string().optional(),
  FEATURED_BRAND_IDS: z.string().optional().default("").transform((val) => {
    return val.split(",").map((id) => id.trim()).filter((id) => id.length > 0).map(Number);
  }),
  CRON_SECRET: z.string().min(1),
  DISABLE_CACHE: z.string().optional(),
  BFF_PUBLIC_URL: z.string().url(),
  OTEL_EXPORTER_OTLP_ENDPOINT: z.string().url().optional(),
  OTEL_EXPORTER_OTLP_HEADERS: z.string().optional(),
  OTEL_SERVICE_NAME: z.string().default("flote-bff"),
  OTEL_SERVICE_VERSION: z.string().default("1.0.0"),
  FILTER_SAMPLE_PRODUCTS: z.string().default("false"),
  MESSAGEBIRD_ACCESS_KEY: z.string().optional(),
  SLACK_TOKEN: z.string().min(1),
  SLACK_NOTIFICATION_CHANNEL_ID: z.string().optional(),
  SLACK_IGNORED_CUSTOMER_IDS: z.string().optional().default("").transform((val) => {
    return val.split(",").map((id) => id.trim()).filter((id) => id.length > 0);
  }),
  TELNYX_API_KEY: z.string().min(1),
  TELNYX_MESSAGING_PROFILE_ID_OTP: z.string().min(1),
  TELNYX_MESSAGING_PROFILE_ID_TRANSACTIONAL: z.string().min(1),
  TELNYX_VERIFY_PROFILE_ID: z.string().min(1),
  TELNYX_PUBLIC_KEY: z.string().min(1),
  TELNYX_WEBHOOK_URL: z.string().min(1),
  TELNYX_WEBHOOK_FAILOVER_URL: z.string().min(1),
  TELNYX_FROM_NUMBER: z.string().optional(),
  // E.164 format sender number for delivery notifications
  // Delivery Notifications
  DELIVERY_NOTIFICATIONS_SMS_ENABLED: z.boolean().optional().default(true),
  DELIVERY_NOTIFICATIONS_SLACK_ENABLED: z.boolean().optional().default(true),
  ACHPC_API_KEY: z.string().min(1),
  ACHPC_API_PASSWORD: z.string().min(1),
  ACHPC_API_USERNAME: z.string().min(1),
  ACHPC_API_URL: z.string().min(1),
  ACH_CHECKOUT_BETA_CUSTOMER_IDS: z.string().optional(),
  // PostHog Analytics
  POSTHOG_API_KEY: z.string().optional(),
  POSTHOG_HOST: z.string().url().default("https://us.i.posthog.com"),
  // Delivery Admin API auth
  DELIVERY_ADMIN_API_KEY: z.string().min(1).optional(),
  // Legacy compatibility key name
  DELIVERY_GATEWAY_API_KEY: z.string().min(1).optional(),
  // Onfleet Integration
  ONFLEET_API_KEY: z.string().min(1).optional(),
  ONFLEET_WEBHOOK_SECRET: z.string().min(1).optional(),
  ONFLEET_ORG_ID: z.string().min(1).optional(),
  // Platform operator org ID (for COD fallback and platform-level operations)
  PLATFORM_OPERATOR_ORG_ID: z.string().uuid().optional(),
  // Sentry Error Tracking
  SENTRY_DSN: z.string().url().optional(),
  // Mapbox Geocoding
  MAPBOX_ACCESS_TOKEN: z.string().optional(),
  // Metrc Cannabis Tracking
  METRC_MT_VENDOR_KEY: z.string().min(1).optional(),
  METRC_MT_USER_KEY: z.string().min(1).optional(),
  METRC_ENCRYPTION_KEY: z.string().length(64).optional(),
  // EasyPost Tracking
  EASYPOST_API_KEY: z.string().min(1).optional(),
  EASYPOST_WEBHOOK_SECRET: z.string().min(1).optional(),
  // Feature flags
  ENABLE_STALE_RESERVATION_ALERTS: z.string().optional(),
  MM_ENABLE_LEGACY_INVOICE_HTTP_FALLBACK: z.enum(["true", "false"]).default("false").transform((val) => val === "true"),
  MM_ENABLE_ROUTE_DUAL_PATH_LOGGING: z.enum(["true", "false"]).default("true").transform((val) => val === "true"),
  MM_ENABLE_LEGACY_INTERNAL_HTTP_FALLBACK: z.enum(["true", "false"]).default("false").transform((val) => val === "true"),
  DELIVERY_SHIPMENT_LEGS_ENABLED: z.enum(["true", "false"]).default("true").transform((val) => val === "true"),
  DELIVERY_LEG_QUEUE_ENABLED: z.enum(["true", "false"]).default("true").transform((val) => val === "true"),
  DELIVERY_DOWNSTREAM_DRAFT_CONFIRMATION_REQUIRED: z.enum(["true", "false"]).default("true").transform((val) => val === "true")
};
var clientSchema = {
  VITE_NODE_ENV: z.enum(["development", "production", "test", "staging"]).default("development"),
  VITE_FLOTE_EMBED_REQUIRE_AUTH: z.string().default("true"),
  VITE_API_URL: z.string().url().optional(),
  VITE_LOG_ENDPOINT: z.string().url().optional(),
  VITE_LOG_LEVEL: z.enum(["trace", "debug", "info", "warn", "error", "fatal"]).default("debug"),
  // PostHog Analytics (client-side)
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().url().optional(),
  // Sentry Error Tracking (client-side)
  VITE_SENTRY_DSN: z.string().url().optional(),
  // Sentry for Embed Widget (separate project for multi-tenant isolation)
  VITE_SENTRY_DSN_EMBED: z.string().url().optional()
};
var env = () => createEnv({
  server: serverSchema,
  client: clientSchema,
  runtimeEnvStrict: {
    // server
    NODE_ENV: process.env.NODE_ENV,
    DIRECT_DATABASE_URL: process.env.DIRECT_DATABASE_URL,
    POOLING_DATABASE_URL: process.env.POOLING_DATABASE_URL,
    DB_FAILOVER_ENABLED: process.env.DB_FAILOVER_ENABLED,
    PRIMARY_DIRECT_DATABASE_URL: process.env.PRIMARY_DIRECT_DATABASE_URL,
    PRIMARY_POOLING_DATABASE_URL: process.env.PRIMARY_POOLING_DATABASE_URL,
    SECONDARY_DIRECT_DATABASE_URL: process.env.SECONDARY_DIRECT_DATABASE_URL,
    SECONDARY_POOLING_DATABASE_URL: process.env.SECONDARY_POOLING_DATABASE_URL,
    DB_ACTIVE_ENDPOINT: process.env.DB_ACTIVE_ENDPOINT,
    DB_FAILOVER_FAILURE_THRESHOLD: process.env.DB_FAILOVER_FAILURE_THRESHOLD,
    DB_FAILOVER_COOLDOWN_MS: process.env.DB_FAILOVER_COOLDOWN_MS,
    DB_HEALTHCHECK_INTERVAL_MS: process.env.DB_HEALTHCHECK_INTERVAL_MS,
    DB_HEALTHCHECK_TIMEOUT_MS: process.env.DB_HEALTHCHECK_TIMEOUT_MS,
    DB_AUTO_FAILBACK_ENABLED: process.env.DB_AUTO_FAILBACK_ENABLED,
    SUPABASE_URL: process.env.SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
    STORAGE_PROVIDER: process.env.STORAGE_PROVIDER,
    STORAGE_PUBLIC_BASE_URL: process.env.STORAGE_PUBLIC_BASE_URL,
    R2_ACCOUNT_ID: process.env.R2_ACCOUNT_ID,
    R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID,
    R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY,
    R2_PRODUCT_IMAGES_BUCKET: process.env.R2_PRODUCT_IMAGES_BUCKET,
    R2_PRODUCT_FILES_BUCKET: process.env.R2_PRODUCT_FILES_BUCKET,
    R2_DELIVERY_PROOF_BUCKET: process.env.R2_DELIVERY_PROOF_BUCKET,
    R2_PRODUCT_IMAGES_PUBLIC_BASE_URL: process.env.R2_PRODUCT_IMAGES_PUBLIC_BASE_URL,
    R2_PRODUCT_FILES_PUBLIC_BASE_URL: process.env.R2_PRODUCT_FILES_PUBLIC_BASE_URL,
    R2_DELIVERY_PROOF_PUBLIC_BASE_URL: process.env.R2_DELIVERY_PROOF_PUBLIC_BASE_URL,
    LOG_LEVEL: process.env.LOG_LEVEL,
    FASTIFY_PORT: process.env.FASTIFY_PORT,
    FASTIFY_HOST: process.env.FASTIFY_HOST,
    FASTIFY_TRUST_PROXY: process.env.FASTIFY_TRUST_PROXY,
    COOKIE_SECRET: process.env.COOKIE_SECRET,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    AUTH_COOKIE_KEY: process.env.AUTH_COOKIE_KEY,
    REDIS_URL: process.env.REDIS_URL,
    REDIS_HOST: process.env.REDIS_HOST,
    REDIS_PORT: process.env.REDIS_PORT,
    REDIS_PASSWORD: process.env.REDIS_PASSWORD,
    REDIS_DB: process.env.REDIS_DB,
    EVENTS_REDIS_URL: process.env.EVENTS_REDIS_URL,
    JOBS_REDIS_URL: process.env.JOBS_REDIS_URL,
    LOG_TARGET: process.env.LOG_TARGET,
    DISABLE_CACHE: process.env.DISABLE_CACHE ?? process.env.NODE_ENV !== "production",
    B2BWAVE_API_KEY: process.env.B2BWAVE_API_KEY,
    B2BWAVE_API_URL: process.env.B2BWAVE_API_URL,
    B2BWAVE_API_TOKEN: process.env.B2BWAVE_API_TOKEN,
    B2BWAVE_APP_CODE: process.env.B2BWAVE_APP_CODE,
    FEATURED_BRAND_IDS: process.env.FEATURED_BRAND_IDS,
    CRON_SECRET: process.env.CRON_SECRET,
    BFF_PUBLIC_URL: process.env.BFF_PUBLIC_URL,
    OTEL_EXPORTER_OTLP_ENDPOINT: process.env.OTEL_EXPORTER_OTLP_ENDPOINT,
    OTEL_EXPORTER_OTLP_HEADERS: process.env.OTEL_EXPORTER_OTLP_HEADERS,
    OTEL_SERVICE_NAME: process.env.OTEL_SERVICE_NAME,
    OTEL_SERVICE_VERSION: process.env.OTEL_SERVICE_VERSION,
    FILTER_SAMPLE_PRODUCTS: process.env.FILTER_SAMPLE_PRODUCTS ?? "false",
    // client
    VITE_NODE_ENV: process.env.VITE_NODE_ENV,
    VITE_FLOTE_EMBED_REQUIRE_AUTH: process.env.VITE_FLOTE_EMBED_REQUIRE_AUTH,
    VITE_API_URL: process.env.VITE_API_URL,
    VITE_LOG_ENDPOINT: process.env.VITE_LOG_ENDPOINT,
    VITE_LOG_LEVEL: process.env.VITE_LOG_LEVEL,
    // Messagebird
    MESSAGEBIRD_ACCESS_KEY: process.env.MESSAGEBIRD_ACCESS_KEY,
    // Slack
    SLACK_IGNORED_CUSTOMER_IDS: process.env.SLACK_IGNORED_CUSTOMER_IDS,
    SLACK_NOTIFICATION_CHANNEL_ID: process.env.SLACK_NOTIFICATION_CHANNEL_ID,
    SLACK_TOKEN: process.env.SLACK_TOKEN,
    // Telnyx
    TELNYX_API_KEY: process.env.TELNYX_API_KEY,
    TELNYX_MESSAGING_PROFILE_ID_OTP: process.env.TELNYX_MESSAGING_PROFILE_ID_OTP,
    TELNYX_MESSAGING_PROFILE_ID_TRANSACTIONAL: process.env.TELNYX_MESSAGING_PROFILE_ID_TRANSACTIONAL,
    TELNYX_VERIFY_PROFILE_ID: process.env.TELNYX_VERIFY_PROFILE_ID,
    TELNYX_PUBLIC_KEY: process.env.TELNYX_PUBLIC_KEY,
    TELNYX_WEBHOOK_URL: process.env.TELNYX_WEBHOOK_URL,
    TELNYX_WEBHOOK_FAILOVER_URL: process.env.TELNYX_WEBHOOK_FAILOVER_URL,
    TELNYX_FROM_NUMBER: process.env.TELNYX_FROM_NUMBER,
    // Delivery Notifications
    DELIVERY_NOTIFICATIONS_SMS_ENABLED: process.env.DELIVERY_NOTIFICATIONS_SMS_ENABLED === "true" || process.env.DELIVERY_NOTIFICATIONS_SMS_ENABLED === void 0,
    DELIVERY_NOTIFICATIONS_SLACK_ENABLED: process.env.DELIVERY_NOTIFICATIONS_SLACK_ENABLED === "true" || process.env.DELIVERY_NOTIFICATIONS_SLACK_ENABLED === void 0,
    // ACHPC
    ACHPC_API_KEY: process.env.ACHPC_API_KEY,
    ACHPC_API_PASSWORD: process.env.ACHPC_API_PASSWORD,
    ACHPC_API_USERNAME: process.env.ACHPC_API_USERNAME,
    ACHPC_API_URL: process.env.ACHPC_API_URL,
    ACH_CHECKOUT_BETA_CUSTOMER_IDS: process.env.ACH_CHECKOUT_BETA_CUSTOMER_IDS,
    // PostHog Analytics
    POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
    POSTHOG_HOST: process.env.POSTHOG_HOST,
    VITE_POSTHOG_KEY: process.env.VITE_POSTHOG_KEY,
    VITE_POSTHOG_HOST: process.env.VITE_POSTHOG_HOST,
    // Sentry (client-side)
    VITE_SENTRY_DSN: process.env.VITE_SENTRY_DSN,
    VITE_SENTRY_DSN_EMBED: process.env.VITE_SENTRY_DSN_EMBED,
    // Delivery Admin API auth
    DELIVERY_ADMIN_API_KEY: process.env.DELIVERY_ADMIN_API_KEY,
    DELIVERY_GATEWAY_API_KEY: process.env.DELIVERY_GATEWAY_API_KEY,
    // Onfleet
    ONFLEET_API_KEY: process.env.ONFLEET_API_KEY,
    ONFLEET_WEBHOOK_SECRET: process.env.ONFLEET_WEBHOOK_SECRET,
    ONFLEET_ORG_ID: process.env.ONFLEET_ORG_ID,
    PLATFORM_OPERATOR_ORG_ID: process.env.PLATFORM_OPERATOR_ORG_ID,
    // Sentry
    SENTRY_DSN: process.env.SENTRY_DSN,
    // Mapbox
    MAPBOX_ACCESS_TOKEN: process.env.MAPBOX_ACCESS_TOKEN,
    // Metrc
    METRC_MT_VENDOR_KEY: process.env.METRC_MT_VENDOR_KEY,
    METRC_MT_USER_KEY: process.env.METRC_MT_USER_KEY,
    METRC_ENCRYPTION_KEY: process.env.METRC_ENCRYPTION_KEY,
    // EasyPost
    EASYPOST_API_KEY: process.env.EASYPOST_API_KEY,
    EASYPOST_WEBHOOK_SECRET: process.env.EASYPOST_WEBHOOK_SECRET,
    // Feature flags
    ENABLE_STALE_RESERVATION_ALERTS: process.env.ENABLE_STALE_RESERVATION_ALERTS,
    MM_ENABLE_LEGACY_INVOICE_HTTP_FALLBACK: process.env.MM_ENABLE_LEGACY_INVOICE_HTTP_FALLBACK,
    MM_ENABLE_ROUTE_DUAL_PATH_LOGGING: process.env.MM_ENABLE_ROUTE_DUAL_PATH_LOGGING,
    MM_ENABLE_LEGACY_INTERNAL_HTTP_FALLBACK: process.env.MM_ENABLE_LEGACY_INTERNAL_HTTP_FALLBACK,
    DELIVERY_SHIPMENT_LEGS_ENABLED: process.env.DELIVERY_SHIPMENT_LEGS_ENABLED,
    DELIVERY_LEG_QUEUE_ENABLED: process.env.DELIVERY_LEG_QUEUE_ENABLED,
    DELIVERY_DOWNSTREAM_DRAFT_CONFIRMATION_REQUIRED: process.env.DELIVERY_DOWNSTREAM_DRAFT_CONFIRMATION_REQUIRED
  },
  clientPrefix: "VITE_",
  emptyStringAsUndefined: true
});
export {
  env
};
